import { useState, useRef } from 'react'
import { db } from '../lib/db'
import { golfapi } from '../lib/golfapi'
import { golfcourseapi } from '../lib/golfcourseapi'
import { searchCourse } from '../lib/searchCourse'

// Shared course picker for the schedule / start-round flows. Searches the CLUBS
// index (the venue reference), then resolves the chosen club to a course —
// locally if the club is already GolfAPI-backed, otherwise enrich-in-place
// (writes the GolfAPI data onto the selected club row, no duplicate rows). A
// single-course club auto-selects; a multi-course club shows a course chooser.
// Calls onSelect(course) with a course row { id, name, external_course_id, … }.
export default function CourseSearch({ onSelect, placeholder = 'Search for a course...' }) {
  const [query, setQuery] = useState('')
  const [clubs, setClubs] = useState([])
  const [searching, setSearching] = useState(false)
  const [resolving, setResolving] = useState(false)
  const [club, setClub] = useState(null)      // a club awaiting a course choice
  const [courses, setCourses] = useState([])  // its courses (only when > 1)
  const [note, setNote] = useState(null)
  // Add-new-venue flow: when the user's course isn't in our seed at all,
  // they tap "Can't find your course? Add it" → the search box morphs into
  // a name-entry field. Task #67 fix (2026-07-13): before creating the
  // manual row, probe search-course for the typed name — search-course's
  // task #66 fallback fires a live GolfAPI/GCA search on local misses.
  // If real candidates come back, show them; the user only writes a
  // manual row when they explicitly reject every candidate.
  const [addMode, setAddMode] = useState(false)
  const [addName, setAddName] = useState('')
  const [probeCandidates, setProbeCandidates] = useState(null)
  const tRef = useRef(null)

  const reset = () => {
    setQuery(''); setClubs([]); setClub(null); setCourses([]); setNote(null)
    setAddMode(false); setAddName('')
    setProbeCandidates(null)
  }

  const onInput = (v) => {
    setQuery(v); setClub(null); setCourses([]); setNote(null)
    if (tRef.current) clearTimeout(tRef.current)
    if (v.trim().length < 2) { setClubs([]); return }
    setSearching(true)
    tRef.current = setTimeout(async () => {
      try {
        const data = await searchCourse({ q: v.trim() })
        setClubs(data.clubs || [])
      } catch { setClubs([]) }
      setSearching(false)
    }, 350)
  }

  // Local courses first (free) for an already-enriched club; else try
  // GolfAPI.io enrich-in-place (POI-rich); if that returns no match OR is
  // degraded (quota), fall through to GolfCourseAPI (broader coverage, no
  // POIs). Courses sourced from GolfCourseAPI score fine but the GPS map
  // degrades to course-pin only — that's a UX concern handled in HoleMap.
  //
  // Shell-detection: short-circuit ONLY when every local course is fully
  // enriched (has hole + tee rows). Pre-fix, a partial enrich (provider
  // returned the name and external_course_id but failed the detail call)
  // left rows with external_course_id set but zero tees/holes; the
  // short-circuit then returned shell rows to the user. Live evidence:
  // Brighton Dale's White Birch + Red Pine were name-only seeded by a
  // GolfAPI call that didn't reach detail; GolfAPI then ran out of
  // credits, the user re-entered to enrich, and the short-circuit
  // returned the shells without trying GolfCourseAPI.
  const isFullyEnriched = (x) =>
    !!x.external_course_id &&
    (x.course_holes_count ?? 0) > 0 &&
    (x.course_tees_count ?? 0) > 0
  // Multi-course venues (e.g. Brighton Dale's 3 nine-hole loops + an 18 combo)
  // get a fallback that only returns ONE course (GolfCourseAPI structurally
  // matches one course per club; see golfcourseapi.js comment). If the
  // fallback returns FEWER courses than the local list has, prefer local —
  // otherwise the picker would auto-select the lone fallback and hide the
  // user's other courses (even if they're shells the user can still see).
  const preferLocal = (local, provider) =>
    provider && provider.length < local.length ? local : provider
  const resolveCourses = async (c) => {
    const local = await db('get_club_courses', { club_id: c.id }).then((r) => r.data || []).catch(() => [])
    if (local.length && local.every(isFullyEnriched)) return local
    const ga = await golfapi('enrich_club', { club_id: c.id, name: c.name, city: c.city, state: c.state }).catch(() => null)
    if (ga && !ga.degraded && ga.courses?.length) return preferLocal(local, ga.courses)
    // GolfAPI didn't match (or was degraded) → try GolfCourseAPI fallback.
    const gca = await golfcourseapi('enrich_club', { club_id: c.id, name: c.name, city: c.city, state: c.state }).catch(() => null)
    if (gca && !gca.degraded && gca.courses?.length) return preferLocal(local, gca.courses)
    return local
  }

  const pickClub = async (c) => {
    setResolving(true); setClubs([]); setNote(null)
    try {
      const list = await resolveCourses(c)
      // A single-course result auto-selects EXCEPT when that course is a
      // manual stub — in the manual case we always want the user to see
      // the "Play in manual mode" CTA so they know they're entering the
      // pars-default-to-4 flow (and that GPS will be set-pin, not POIs).
      // Skipping the CTA on a pre-existing manual course was confusing —
      // user reported it just "didn't ask to play manually" the 2nd time.
      const onlyOne = list.length === 1
      const isManualOnly = onlyOne && list[0]?.source === 'manual'
      if (onlyOne && !isManualOnly) { onSelect({ ...list[0], club_name: c.name, city: c.city, state: c.state }); reset() }
      else if (list.length > 1) { setClub(c); setCourses(list) }
      else { setClub(c); setCourses([]); setNote('Course details unavailable right now.') }
    } catch { setClub(c); setCourses([]); setNote('Could not load courses.') }
    setResolving(false)
  }

  const pickCourse = (course) => { onSelect({ ...course, club_name: club?.name, city: club?.city, state: club?.state }); reset() }

  // Manual-mode fallback: when no provider has data for the venue the user
  // picked, give them a path to still play. Creates a 'manual' course stub
  // attached to the LOCAL clubs row (idempotent — repeat clicks reuse the
  // same stub), defaults to 18 holes / par 4 across the board. Phase 2 adds
  // a par-picker after this point; Phase 3 adds GPS-set-pin.
  const startManualMode = async () => {
    if (!club) return
    setResolving(true)
    try {
      const { data } = await db('create_manual_course', { club_id: club.id, name: club.name, holes_count: 18 })
      if (data) {
        onSelect({ ...data, club_name: club.name, city: club.city, state: club.state })
        reset()
      } else {
        setNote('Could not start a manual round. Please try again.')
      }
    } catch {
      setNote('Could not start a manual round. Please try again.')
    }
    setResolving(false)
  }

  // Add-new-venue path: user typed a name we don't have, taps "Add it",
  // enters the canonical name → we PROBE providers first (task #67, via
  // search-course's task #66 fallback), and only fall through to a
  // manual insert when nothing authoritative comes back OR the user
  // explicitly rejects every candidate.
  //
  // City/state aren't collected here (user is typically teeing off and
  // doesn't want a form) — the lat/lng captured during the GPS Set-Pin
  // flow on hole 1 lets an admin sweep classify what venue this is later.
  const submitNewVenue = async () => {
    const name = addName.trim()
    if (name.length < 2) return
    setResolving(true)
    try {
      // Probe: search-course will run the local ILIKE + fire a provider
      // search on a local miss (or manual-only local hit) and return
      // whatever it finds. Any non-manual candidate means we should show
      // the "Did you mean…" step before writing a manual stub.
      const probeJson = await searchCourse({ q: name }).catch(() => ({ clubs: [] }))
      const authoritativeCandidates = (probeJson.clubs || []).filter(
        c => c.source && c.source !== 'manual'
      )
      if (authoritativeCandidates.length > 0) {
        setProbeCandidates(authoritativeCandidates)
        setResolving(false)
        return
      }
      await createManualNow(name)
    } catch {
      setNote('Could not check for existing venues. Try again.')
      setResolving(false)
    }
  }

  // Extracted from the original submitNewVenue so we can reuse it after
  // the user rejects every provider candidate.
  const createManualNow = async (name) => {
    setResolving(true)
    try {
      const clubRes = await db('create_manual_club', { name })
      const newClub = clubRes?.data
      if (!newClub?.id) {
        setNote('Could not add that venue. Please try again.')
        setResolving(false)
        return
      }
      const courseRes = await db('create_manual_course', { club_id: newClub.id, name, holes_count: 18 })
      if (courseRes?.data) {
        onSelect({ ...courseRes.data, club_name: newClub.name })
        reset()
      } else {
        setNote('Could not start a manual round. Please try again.')
      }
    } catch {
      setNote('Could not add that venue. Please try again.')
    }
    setResolving(false)
  }

  const beginAddMode = () => {
    setAddMode(true)
    setAddName(query) // seed with what they were searching for
    setClubs([]); setClub(null); setCourses([]); setNote(null)
  }

  // "Can't find your course?" hint visibility: show whenever the user has
  // typed something meaningful and either got no results OR is staring at a
  // list that doesn't include their venue. We keep showing it WITH the
  // results list (not only on zero hits) because a wrong-name seed can
  // out-rank the user's real venue (e.g. "Bristol Oaks Country Club"
  // entries above their actual local "Bristol Oaks Golf Club" — they may
  // need "Add it" even when the dropdown is full).
  const showCantFindHint = !addMode && !club && query.trim().length >= 2 && !searching && !resolving

  if (addMode) {
    // "Did you mean…" step: renders when the probe found authoritative
    // candidates for the typed name. User picks one (treated exactly like
    // a normal club selection → resolveCourses runs) OR opts to create a
    // manual entry anyway.
    if (probeCandidates && probeCandidates.length > 0) {
      return (
        <div>
          <p style={{ fontSize: '0.78rem', color: 'var(--gray-300)', margin: '0 0 8px' }}>
            Before we create a new manual entry — did you mean one of these?
          </p>
          <div style={listBox}>
            {probeCandidates.map((c, i) => (
              <button
                key={c.id || i}
                onClick={() => {
                  setProbeCandidates(null)
                  pickClub(c)
                }}
                style={rowBtn(i, probeCandidates.length)}
              >
                <span style={{ fontWeight: 600 }}>{c.name}</span>
                {(c.city || c.state) && (
                  <span className="text-muted" style={{ fontSize: '0.78rem', marginLeft: 8 }}>
                    {[c.city, c.state].filter(Boolean).join(', ')}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              onClick={() => setProbeCandidates(null)}
              disabled={resolving}
              style={{
                flex: 1, padding: '10px 12px',
                background: 'transparent', border: '1px solid var(--green-mid)',
                color: 'var(--cream)', borderRadius: 'var(--radius)',
                fontSize: '0.85rem', cursor: resolving ? 'default' : 'pointer',
              }}
            >
              ← Back
            </button>
            <button
              onClick={() => { setProbeCandidates(null); createManualNow(addName.trim()) }}
              disabled={resolving}
              style={{
                flex: 2, padding: '10px 12px',
                background: 'var(--gold)', color: 'var(--green-deep)',
                border: 'none', borderRadius: 'var(--radius)',
                fontWeight: 700, fontSize: '0.85rem',
                cursor: resolving ? 'default' : 'pointer',
              }}
            >
              {resolving ? 'Setting up…' : 'None of these — add as new →'}
            </button>
          </div>
          {note && <p className="text-xs text-muted" style={{ marginTop: 6, color: 'var(--red)' }}>{note}</p>}
        </div>
      )
    }

    return (
      <div>
        <p style={{ fontSize: '0.78rem', color: 'var(--gray-300)', margin: '0 0 6px' }}>
          Add a venue we don't have. Just the name — we'll capture the
          location when you set your first pin on the course.
        </p>
        <input
          className="input"
          value={addName}
          onChange={(e) => setAddName(e.target.value)}
          placeholder="Venue name (e.g. My Backyard Loop)"
          autoFocus
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            onClick={() => { setAddMode(false); setAddName('') }}
            disabled={resolving}
            style={{
              flex: 1, padding: '10px 12px',
              background: 'transparent', border: '1px solid var(--green-mid)',
              color: 'var(--cream)', borderRadius: 'var(--radius)',
              fontSize: '0.85rem', cursor: resolving ? 'default' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={submitNewVenue}
            disabled={resolving || addName.trim().length < 2}
            style={{
              flex: 2, padding: '10px 12px',
              background: addName.trim().length >= 2 ? 'var(--gold)' : 'var(--green-mid)',
              color: addName.trim().length >= 2 ? 'var(--green-deep)' : 'var(--gray-300)',
              border: 'none', borderRadius: 'var(--radius)',
              fontWeight: 700, fontSize: '0.85rem',
              cursor: addName.trim().length >= 2 && !resolving ? 'pointer' : 'default',
            }}
          >
            {resolving ? 'Checking…' : 'Continue →'}
          </button>
        </div>
        {note && <p className="text-xs text-muted" style={{ marginTop: 6, color: 'var(--red)' }}>{note}</p>}
      </div>
    )
  }

  return (
    <>
      <input className="input" value={query} onChange={(e) => onInput(e.target.value)} placeholder={placeholder} />
      {(searching || resolving) && (
        <p className="text-xs text-muted" style={{ marginTop: 6 }}>{resolving ? 'Loading courses…' : 'Searching…'}</p>
      )}
      {note && <p className="text-xs text-muted" style={{ marginTop: 6 }}>{note}</p>}

      {/* Manual-mode fallback CTA — only shown after we've tried both providers
          and come up empty (note === 'Course details unavailable right now.').
          Tells the user they can still play this venue without provider data,
          and offers a button to start a manual round attached to the same club
          they tapped. Other note states ("Could not load courses.") aren't
          definitive misses, so the CTA stays hidden for those. */}
      {club && courses.length === 0 && note === 'Course details unavailable right now.' && (
        <div style={{
          marginTop: 8, padding: '10px 12px',
          background: 'var(--green-deep)', border: '1px solid var(--gold)',
          borderRadius: 'var(--radius)',
        }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--cream)', margin: '0 0 8px' }}>
            You can still play <strong>{club.name}</strong> in manual mode with GPS targeting.
            We'll set up an 18-hole round; you fill in pars on the next screen.
          </p>
          <button
            onClick={startManualMode}
            disabled={resolving}
            style={{
              display: 'block', width: '100%', padding: '10px 12px',
              border: 'none', borderRadius: 'var(--radius)',
              background: 'var(--gold)', color: 'var(--green-deep)',
              fontWeight: 700, fontSize: '0.85rem', cursor: resolving ? 'default' : 'pointer',
            }}
          >
            {resolving ? 'Setting up…' : 'Play in manual mode →'}
          </button>
        </div>
      )}

      {/* multi-course club → course chooser */}
      {club && courses.length > 1 && (
        <div style={listBox}>
          <div style={{ padding: '8px 14px', fontSize: '0.78rem', color: 'var(--gray-400)' }}>{club.name} — pick a course</div>
          {courses.map((c, i) => (
            <button key={c.id || i} onClick={() => pickCourse(c)} style={rowBtn(i, courses.length)}>
              <span style={{ fontWeight: 600 }}>{c.name}</span>
              {c.holes ? <span className="text-muted" style={{ fontSize: '0.78rem', marginLeft: 8 }}>{c.holes} holes</span> : null}
            </button>
          ))}
        </div>
      )}

      {/* club results */}
      {!club && clubs.length > 0 && (
        <div style={listBox}>
          {clubs.map((c, i) => (
            <button key={c.id || i} onClick={() => pickClub(c)} style={rowBtn(i, clubs.length)}>
              <span style={{ fontWeight: 600 }}>{c.name}</span>
              {(c.city || c.state) && (
                <span className="text-muted" style={{ fontSize: '0.78rem', marginLeft: 8 }}>
                  {[c.city, c.state].filter(Boolean).join(', ')}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* "Can't find your course?" sits BELOW the results so the user can
          scan what we have first, then opt to add their venue if it's
          really missing (or named differently than they searched). */}
      {showCantFindHint && (
        <p style={{ fontSize: '0.78rem', color: 'var(--gray-300)', margin: '8px 0 0' }}>
          Can't find your course?{' '}
          <button
            onClick={beginAddMode}
            style={{
              background: 'transparent', border: 'none', padding: 0,
              color: 'var(--gold)', textDecoration: 'underline',
              cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
            }}
          >
            Click here to add it.
          </button>
        </p>
      )}
    </>
  )
}

// Secondary line for a selected course: the course name (only when it differs
// from the club name — e.g. "Blue Spruce" under "Brighton Dale Links"), location,
// and par. The club name is shown as the primary line by the caller.
export function courseSubtitle(c) {
  if (!c) return ''
  const parts = []
  if (c.name && c.club_name && c.name !== c.club_name) parts.push(c.name)
  const loc = [c.city, c.state].filter(Boolean).join(', ')
  if (loc) parts.push(loc)
  if (c.par) parts.push(`Par ${c.par}`)
  return parts.join(' · ')
}

const listBox = { marginTop: 4, borderRadius: 'var(--radius)', border: '1px solid var(--green-mid)', overflow: 'hidden' }
const rowBtn = (i, n) => ({
  display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px',
  background: 'var(--green-dark)', border: 'none',
  borderBottom: i < n - 1 ? '1px solid var(--green-mid)' : 'none',
  color: 'var(--cream)', cursor: 'pointer', fontSize: '0.9rem',
})
